import useTranslate from '@Utils/hooks/useTranslate';
import React, { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import { ParagraphS } from '@Components/Typography/Typography.styled';
import OtpInputs from '@Components/OtpInputs/OtpInputs';
import { Formik, FormikProps } from 'formik';
import { getTimerFormat } from '@Utils/date/moment';
import Divider from '@Components/Divider/Divider';
import Icon from '@Components/Icon/Icon';
import {
	OTPCardContainer,
	TitleWrapper,
	SubheaderContainer,
	SubheaderWrapper,
	OTPInputsContainer,
	Horizontal,
	ResendButtonContainer,
	ButtonsContainer,
	SubheaderBoldWrapper,
	LoadingContainer,
	SuccessWrapper,
} from './OTPDialog.styled';
import {
	PrimaryLargeButtonFlex,
	TertiaryLargeButtonFlex,
	TertiaryMediumButtonAuto,
} from '@Components/controls/Button/Button';
import useOtpTimer from '@Utils/hooks/useOtpTimer';
import { setModal } from '@Redux/modules/modal/actions';
import { useDispatch } from 'react-redux';
import { AuthenticationService } from '@Services/authentication/authenticationService';
import BusyIndicator from '@Components/BusyIndicator/BusyIndicator';
import { NO_TRANSLATION } from '@Redux/modules/settings/language/selectors';

export interface IOtpFormikFields {
	otp: string[];
}

const DEFAULT_TIME = 180000;
const inputs: RefObject<HTMLInputElement>[] = [];

const CONFIRM_OTP_BI = 'confirmOTP';

const OTPDialog: FunctionComponent<types.cards.IOTPDialogCardProps> = ({
	params: { corpUser, login, errorCode, teleSales },
}) => {
	const { translate } = useTranslate();
	const [otpArrayCode] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [otpSent, setOtpSent] = useState(true);

	const otpRef = useRef<FormikProps<IOtpFormikFields>>(null);

	const createRefs = (numberOfInputs: number) => {
		for (let index = 0; index < numberOfInputs; index++) {
			inputs[index] = React.createRef();
		}
		return inputs;
	};
	const onClickResend = () => {
		setLoading(true);

		AuthenticationService.resendOtp(login, corpUser).subscribe(
			(response) => {
				if (response.success) {
					setLoading(false);
					setOtpSent(true);
					setTimer({ time: DEFAULT_TIME, activated: true });
					otpRef.current?.setFieldError('otp', '');
					otpRef.current?.setFieldValue('otp', []);
				} else {
					setLoading(false);
					setOtpSent(false);
				}
			},
			() => {
				setLoading(false);
				setOtpSent(false);
			}
		);
	};
	const dispatch = useDispatch();
	const {
		timer: { time, activated },
		setTimer,
	} = useOtpTimer();

	useEffect(() => {
		setTimer({ activated: true, time: DEFAULT_TIME });
		return () => {
			setTimer({ time: 0, activated: false });
		};
	}, []);
	useEffect(() => {
		if (errorCode === 'OTP') setOtpSent(true);
		else setOtpSent(false);
	}, [errorCode]);
	const confirm = () => {
		const token = String(otpRef.current?.values.otp ?? '').replace(/,/g, '');

		if (token.length === 5) {
			setLoading(true);
			AuthenticationService.validateOtp(login, token, corpUser, teleSales).subscribe(
				() => {
					setLoading(false);
				},
				(errors) => {
					otpRef.current?.setFieldError(
						'otp',
						translate(`mfa.otp.error.${errors?.response?.errors[0]?.code}`) === NO_TRANSLATION
							? translate(`mfa.otp.error.USER_SECURE_AUTH_CACHING_EXCEPTION_OCCURRED`)
							: translate(`mfa.otp.error.${errors?.response?.errors[0]?.code}`)
					);
					setLoading(false);
				}
			);
		} else {
			setLoading(false);
			otpRef.current?.setFieldError('otp', translate('otp-card.error'));
		}
	};
	return (
		<OTPCardContainer isXS={false}>
			<ConditionalRender
				show={loading}
				onTrue={() => (
					<LoadingContainer>
						<BusyIndicator listener={true} />
					</LoadingContainer>
				)}
			/>
			<TitleWrapper>{translate('2fa.otp.header')}</TitleWrapper>
			<OTPInputsContainer>
				<Formik<IOtpFormikFields>
					initialValues={{ otp: otpArrayCode }}
					onSubmit={() => {
						confirm();
					}}
					innerRef={otpRef}
				>
					{({ setFieldValue, values, errors }) => (
						<>
							<OtpInputs
								otpArrayCode={values.otp}
								inputRefs={createRefs(5)}
								onChange={(value) => {
									setFieldValue('otp', value.split(','));
								}}
								error={!!errors.otp}
							/>
							<ConditionalRender
								show={!!errors.otp}
								onTrue={() => (
									<>
										<Horizontal>
											<>
												<Icon name="warning" fill="warning" width={24} height={24} />
												<ParagraphS color="warning">{errors.otp}</ParagraphS>
											</>
										</Horizontal>
									</>
								)}
								onFalse={() => <Divider marginBottom={54} withoutLine />}
							/>
						</>
					)}
				</Formik>
			</OTPInputsContainer>
			{otpSent && (
				<ConditionalRender
					show={time === 0}
					onTrue={() => (
						<ResendButtonContainer>
							<TertiaryMediumButtonAuto
								uniqueId="otp-card.resend"
								onClick={() => {
									try {
										onClickResend();
									} catch {}
								}}
							>
								{translate('otp-card.resend')}
							</TertiaryMediumButtonAuto>
							<Horizontal>
								<>
									<Icon name="warning" fill="warning" width={24} height={24} />

									<ParagraphS color="warning">{translate(`mfa.otp.error.OTP_NOT_SENT`)}</ParagraphS>
								</>
							</Horizontal>
						</ResendButtonContainer>
					)}
					onFalse={() => (
						<>
							<Horizontal>
								<Icon name="success" fill="green" width={24} height={24} stroke="green" />
								<SuccessWrapper>{translate(`mfa.otp.success.OTP`)}</SuccessWrapper>
							</Horizontal>
							<SubheaderContainer>
								<SubheaderWrapper>{translate('otp-card.valid')}</SubheaderWrapper>
								<SubheaderBoldWrapper>{getTimerFormat(time)}.</SubheaderBoldWrapper>
							</SubheaderContainer>
						</>
					)}
				/>
			)}
			<ConditionalRender
				show={!otpSent}
				onTrue={() => (
					<Horizontal>
						<>
							<Icon name="warning" fill="warning" width={24} height={24} />
							<ParagraphS color="warning">{translate(`mfa.otp.error.OTP_FAIL`)}</ParagraphS>
						</>
					</Horizontal>
				)}
			/>

			<ButtonsContainer>
				<PrimaryLargeButtonFlex
					uniqueId="otp-card.confirm"
					onClick={() => {
						confirm();
					}}
					disabled={!otpSent}
					listener={CONFIRM_OTP_BI}
				>
					{translate('otp-card.confirm')}
				</PrimaryLargeButtonFlex>
				<TertiaryLargeButtonFlex
					uniqueId="otp-card.cancel"
					onClick={() => {
						dispatch(setModal({ closeAllModals: true, type: null }));
					}}
					listener={CONFIRM_OTP_BI}
				>
					{translate('otp-card.cancel')}
				</TertiaryLargeButtonFlex>
			</ButtonsContainer>
		</OTPCardContainer>
	);
};

export default OTPDialog;
